/**
 * useDiscoveryProgress Hook
 * Manages discovery progress state and provides methods to update it
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  initializeDiscoveryProgress,
  updateTopicDiscovery,
  saveDiscoveryProgress,
  loadDiscoveryProgress,
  getCurrentICPRequirements,
  getDiscoveryCompletionPercentage,
  getNextDiscoveryTopic,
  isTopicDiscoveryComplete,
  mergeDiscoveryProgress,
  DISCOVERY_STATUS
} from '../utils/discoveryManager';

/**
 * Custom hook for managing discovery progress
 * @param {string} sessionId - Current session ID
 * @param {Array} initialICPRequirements - Initial ICP requirements from runAIMatches
 * @param {Array} discoveryOrder - Discovery order for the current role type
 * @returns {Object} Discovery progress state and methods
 */
export function useDiscoveryProgress(sessionId, initialICPRequirements = [], discoveryOrder = []) {
  const [discoveryProgress, setDiscoveryProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);
  
  // Load discovery progress on mount or session change
  useEffect(() => {
    if (!sessionId) {
      setDiscoveryProgress(null);
      setIsLoading(false);
      return;
    }
    
    const loadProgress = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Try to load from database first
        const savedProgress = await loadDiscoveryProgress(sessionId);
        
        if (savedProgress) {
          console.log('Loaded existing discovery progress:', savedProgress);
          setDiscoveryProgress(savedProgress);
        } else if (initialICPRequirements && initialICPRequirements.length > 0) {
          // Initialize new progress if none exists
          console.log('Initializing new discovery progress with ICP requirements:', initialICPRequirements);
          const newProgress = initializeDiscoveryProgress(initialICPRequirements);
          setDiscoveryProgress(newProgress);
          
          // Save the initial progress
          await saveDiscoveryProgress(sessionId, newProgress);
        } else {
          // No saved progress and no initial requirements
          console.log('No discovery progress found and no initial requirements provided');
          setDiscoveryProgress(null);
        }
      } catch (err) {
        console.error('Error loading discovery progress:', err);
        setError(err.message);
        
        // Fall back to initializing if load fails
        if (initialICPRequirements && initialICPRequirements.length > 0) {
          const newProgress = initializeDiscoveryProgress(initialICPRequirements);
          setDiscoveryProgress(newProgress);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProgress();
  }, [sessionId]);
  
  // Update with new ICP requirements if they change
  useEffect(() => {
    if (!discoveryProgress && initialICPRequirements && initialICPRequirements.length > 0 && !isLoading) {
      console.log('Initializing discovery progress with new ICP requirements');
      const newProgress = initializeDiscoveryProgress(initialICPRequirements);
      setDiscoveryProgress(newProgress);
    }
  }, [initialICPRequirements, discoveryProgress, isLoading]);
  
  // Auto-save discovery progress
  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(async () => {
      if (discoveryProgress && sessionId && 
          JSON.stringify(discoveryProgress) !== JSON.stringify(lastSavedRef.current)) {
        setIsSaving(true);
        try {
          await saveDiscoveryProgress(sessionId, discoveryProgress);
          lastSavedRef.current = discoveryProgress;
          console.log('Auto-saved discovery progress');
        } catch (err) {
          console.error('Error auto-saving discovery progress:', err);
        } finally {
          setIsSaving(false);
        }
      }
    }, 2000); // Auto-save after 2 seconds of inactivity
  }, [discoveryProgress, sessionId]);
  
  // Schedule auto-save when progress changes
  useEffect(() => {
    if (discoveryProgress && sessionId) {
      scheduleAutoSave();
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [discoveryProgress, sessionId, scheduleAutoSave]);
  
  // Update discovery for a topic
  const updateDiscovery = useCallback((topic, updates) => {
    if (!discoveryProgress) {
      console.warn('No discovery progress to update');
      return;
    }
    
    const updatedProgress = updateTopicDiscovery(discoveryProgress, topic, updates);
    setDiscoveryProgress(updatedProgress);
  }, [discoveryProgress]);
  
  // Mark topic as complete
  const markTopicComplete = useCallback((topic, boolStr, componentData = null) => {
    updateDiscovery(topic, {
      status: DISCOVERY_STATUS.DISCOVERY_DONE,
      bool_str: boolStr,
      component_data: componentData
    });
  }, [updateDiscovery]);
  
  // Mark topic as in progress
  const markTopicInProgress = useCallback((topic) => {
    updateDiscovery(topic, {
      status: DISCOVERY_STATUS.IN_PROGRESS
    });
  }, [updateDiscovery]);
  
  // Get current ICP requirements
  const getICPRequirements = useCallback(() => {
    if (!discoveryProgress) return [];
    return getCurrentICPRequirements(discoveryProgress);
  }, [discoveryProgress]);
  
  // Get completion percentage
  const getCompletionPercentage = useCallback(() => {
    if (!discoveryProgress) return 0;
    return getDiscoveryCompletionPercentage(discoveryProgress);
  }, [discoveryProgress]);
  
  // Get next topic
  const getNextTopic = useCallback(() => {
    if (!discoveryProgress || !discoveryOrder) return null;
    return getNextDiscoveryTopic(discoveryProgress, discoveryOrder);
  }, [discoveryProgress, discoveryOrder]);
  
  // Check if topic is complete
  const isTopicComplete = useCallback((topic) => {
    if (!discoveryProgress) return false;
    return isTopicDiscoveryComplete(discoveryProgress, topic);
  }, [discoveryProgress]);
  
  // Force save
  const forceSave = useCallback(async () => {
    if (!discoveryProgress || !sessionId) return;
    
    setIsSaving(true);
    try {
      await saveDiscoveryProgress(sessionId, discoveryProgress);
      lastSavedRef.current = discoveryProgress;
      console.log('Force saved discovery progress');
    } catch (err) {
      console.error('Error saving discovery progress:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [discoveryProgress, sessionId]);
  
  // Merge external updates
  const mergeUpdates = useCallback((updates) => {
    if (!discoveryProgress) {
      console.warn('No discovery progress to merge updates into');
      return;
    }
    
    const mergedProgress = mergeDiscoveryProgress(discoveryProgress, updates);
    setDiscoveryProgress(mergedProgress);
  }, [discoveryProgress]);
  
  // Reset discovery progress
  const resetProgress = useCallback(async () => {
    if (!sessionId) return;
    
    const newProgress = initializeDiscoveryProgress(initialICPRequirements || []);
    setDiscoveryProgress(newProgress);
    
    try {
      await saveDiscoveryProgress(sessionId, newProgress);
      console.log('Reset discovery progress');
    } catch (err) {
      console.error('Error resetting discovery progress:', err);
      setError(err.message);
    }
  }, [sessionId, initialICPRequirements]);
  
  return {
    // State
    discoveryProgress,
    isLoading,
    error,
    isSaving,
    
    // Methods
    updateDiscovery,
    markTopicComplete,
    markTopicInProgress,
    getICPRequirements,
    getCompletionPercentage,
    getNextTopic,
    isTopicComplete,
    forceSave,
    mergeUpdates,
    resetProgress,
    
    // Constants
    DISCOVERY_STATUS
  };
}

export default useDiscoveryProgress;
